import { BrowserRouter as Router, Routes, Route, Outlet } from "react-router";
import { useCallback, useEffect, useState } from "react";
import SignIn from "./pages/AuthPages/SignIn";
import SignOutSignIn from "./pages/AuthPages/SignOutSignIn";
import NotFound from "./pages/OtherPage/NotFound";
import AppLayout, { AppShell, AppLayoutWide } from "./layout/AppLayout";
import { SidebarProvider } from "./context/SidebarContext";
import { ScrollToTop } from "./components/common/ScrollToTop";
import { ContentsquareUserSegment } from "./components/common/ContentsquareUserSegment";
import { ReloadOnNewVersion } from "./components/common/ReloadOnNewVersion";
import { useStateValue } from './context/StateValueContext'
import Protected from './components/Protected'

import { auth } from '../config/firebase.js';
import Dashboard from "./pages/Dashboard.js";
import Reports from "./pages/Reports.js";
import DisputesRouteGuard from "./components/DisputesRouteGuard.js";
import DisputeEvidence from "./pages/DisputeEvidence.js";
import AdminEvidenceActivity from "./pages/AdminEvidenceActivity.js";
import EvidenceBuilder from "./pages/EvidenceBuilder.js";
import PaypalInquiries from "./pages/PaypalInquiries/PaypalInquiries.js";
import OnboardingDashboard from "./pages/Onboarding/OnboardingDashboard.js";
import OnboardingNew from "./pages/Onboarding/OnboardingNew.js";
import OnboardingChecklist from "./pages/Onboarding/OnboardingChecklist.js";
import FieldTheme from "./pages/Onboarding/FieldTheme.js";
import Integrations from "./pages/Integrations.js";
import AdminCompanyDetails from "./pages/AdminCompanyDetails.js";
import ResetPassword from "./pages/AuthPages/ResetPassword.js";
import SetPassword from "./pages/AuthPages/SetPassword.js";
import ShopifyInstallError from "./pages/ShopifyInstallError.js";
import { User } from "./types/user.js";
import { CLIENT_ROLES } from "./types/companyUser.js";
import { PERMISSIONS } from "./constants/permissions.js";
import { MemberCompany } from "./types/company.js";
import { useApi } from "./hooks/useApi.js";
import { useRefreshCompanies } from "./hooks/useRefreshCompanies.js";
import { useFetchCompanySettings } from "./hooks/useCompanySettings.js";
import FirstPage from "./components/FirstPage.js";
import AdminPanel from "./pages/AdminPanel.js";
import PaymentSearch from "./pages/PaymentSearch.js";
import AlertsDashboard from "./pages/Alerts/Dashboard.js";
import EthocaSwitcher from "./pages/Alerts/EthocaSwitcher.js";
import CdrnSwitcher from "./pages/Alerts/CdrnSwitcher.js";
import EthocaSettingsPage from "./pages/Alerts/EthocaSettingsPage.js";
import CdrnSettingsPage from "./pages/Alerts/CdrnSettingsPage.js";
import RdrNoticesSettingsPage from "./pages/Alerts/RdrNoticesSettingsPage.js";
import RdrSwitcher from "./pages/Alerts/RdrSwitcher";
import FraudSwitcher from "./pages/Alerts/FraudSwitcher";
import DisputeSwitcher from "./pages/Alerts/DisputeSwitcher";
import AlertLogs from "./pages/Alerts/AlertLogs";
import EnrolmentSwitcher from "./pages/Alerts/EnrolmentSwitcher";
import OrderInsightRouteGuard from "./components/OrderInsightRouteGuard";
import CeNotices from "./pages/Alerts/CeNotices";
import BillingCredits from "./pages/Alerts/BillingCredits";
import VerifiPortalImport from "./pages/VerifiPortalImport";
import ShopifyPaymentsCachePage from "./pages/ShopifyPaymentsCachePage";
import CheckoutChampTransactionsPage from "./pages/CheckoutChampTransactionsPage";
import CrmConnectorTransactionsPage from "./pages/CrmConnectorTransactionsPage";
import AuthorizeNetTransactionsPage from "./pages/AuthorizeNetTransactionsPage";
import ClientBilling from "./pages/ClientBilling";
import ClientApiKeys from "./pages/ClientApiKeys";
import * as Sentry from "@sentry/react";
import { requiresGoogleSignIn } from "./utils/authUtils";

// Create SentryRoutes at module level to prevent remounting on App re-renders
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

export default function App() {
  // Check auth state synchronously on initial load to prevent flash
  const [isSignedIn, setIsSignedIn] = useState(() => !!auth.currentUser);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [, dispatch] = useStateValue();
  const { get } = useApi();
  const refreshCompanies = useRefreshCompanies();
  const fetchCompanySettings = useFetchCompanySettings();

  const fetchCompanies = useCallback(async (user: User) => {
    if (user?.companyId !== 1) return;
    await refreshCompanies();
  }, [refreshCompanies]);

  const fetchMemberCompanies = useCallback(async (user: User) => {
    try {
      if (!CLIENT_ROLES.has(user?.role ?? '')) {
        return;
      }

      const response = await get<{ companies: MemberCompany[] }>(`/api/users/me/companies`);
      dispatch({
        type: "SET_MEMBER_COMPANIES",
        memberCompanies: response.companies,
      });
    } catch (err) {
      console.error('Failed to fetch member companies:', err);
    }
  }, [get, dispatch]);

  useEffect(() => {
    // will only run once when the app component loads...

    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        // Force sign-out for @chargemont.com users logged in via password
        const hasGoogleProvider = authUser.providerData.some(p => p.providerId === 'google.com');
        if (authUser.email && requiresGoogleSignIn(authUser.email) && !hasGoogleProvider) {
          await auth.signOut();
          return;
        }

        // the user just logged in / the user was logged in

        const idTokenResult = await authUser.getIdTokenResult(true);

        if (idTokenResult.claims.mustChangePassword === true) {
          setMustChangePassword(true);
          setIsSignedIn(true);
          setAuthInitialized(true);
          return;
        }

        setMustChangePassword(false);
        const user = {
          uid: authUser.uid,
          email: authUser.email,
          role: idTokenResult.claims.role || "client_admin",
          permissions: idTokenResult.claims.permissions || [],
          companyId: Number(idTokenResult.claims.companyId),
        } as User;

        Sentry.setUser({
          id: user.uid,
          email: user.email,
          companyId: user.companyId,
        });

        dispatch({
          type: "SET_USER",
          user: user,
        });
        setIsSignedIn(true);
        setAuthInitialized(true);

        fetchCompanies(user);
        fetchMemberCompanies(user);
        fetchCompanySettings(user);
      } else {
        // the user is logged out
        dispatch({ type: "RESET_USER" });
        setIsSignedIn(false);
        setAuthInitialized(true);
        Sentry.setUser(null);
      }
    });

    return () => unsubscribe();
  }, [dispatch, fetchCompanies, fetchMemberCompanies, fetchCompanySettings]);

  return (
    <>
      <Router>
      <ScrollToTop />
      <ContentsquareUserSegment />
      <ReloadOnNewVersion />
        <SentryRoutes>
          {/* Shared shell: sidebar + header mount once */}
          <Route element={<Protected isSignedIn={isSignedIn} authInitialized={authInitialized} mustChangePassword={mustChangePassword}><SidebarProvider><AppShell /></SidebarProvider></Protected>}>
            {/* Default Layout (max-width capped) */}
            <Route element={<AppLayout />}>
              <Route path="/" element={<FirstPage />}></Route>
              <Route index path="/dashboard" element={<Protected permission={PERMISSIONS.DASHBOARD}><Dashboard /></Protected>} />
              <Route path="/disputes" element={<Protected permission={PERMISSIONS.DISPUTE_READ}><DisputesRouteGuard /></Protected>} />
              <Route path="/payment-search" element={<Protected permission={PERMISSIONS.PAYMENT_SEARCH}><PaymentSearch /></Protected>} />
              <Route path="/alerts/dashboard" element={<Protected permission={PERMISSIONS.ALERTS_ALL}><AlertsDashboard /></Protected>} />
              <Route path="/alerts/ethoca-settings" element={<Protected permission={PERMISSIONS.ALERTS_SETTINGS}><EthocaSettingsPage /></Protected>} />
              <Route path="/alerts/cdrn-settings" element={<Protected permission={PERMISSIONS.ALERTS_SETTINGS}><CdrnSettingsPage /></Protected>} />
              <Route path="/alerts/verifi-notices-settings" element={<Protected permission={PERMISSIONS.ALERTS_SETTINGS}><RdrNoticesSettingsPage /></Protected>} />
              <Route path="/companies" element={<Protected permission={PERMISSIONS.COMPANIES_WRITE_ALL}><AdminCompanyDetails /></Protected>} />
              <Route path="/integrations" element={<Protected permission={PERMISSIONS.ACCOUNTS_OWN}><Integrations /></Protected>} />
              <Route path="/admin-panel" element={<Protected permission={PERMISSIONS.ADMIN_PANEL}><AdminPanel /></Protected>} />
              <Route path="/verifi-import" element={<Protected permission={PERMISSIONS.ALERTS_SETTINGS}><VerifiPortalImport /></Protected>} />
              <Route path="/admin/shopify-payments-cache" element={<Protected permission={PERMISSIONS.ADMIN_PANEL}><ShopifyPaymentsCachePage /></Protected>} />
              <Route path="/admin/checkout-champ-transactions" element={<Protected permission={PERMISSIONS.ADMIN_PANEL}><CheckoutChampTransactionsPage /></Protected>} />
              <Route path="/admin/crm-connector-transactions" element={<Protected permission={PERMISSIONS.ADMIN_PANEL}><CrmConnectorTransactionsPage /></Protected>} />
              <Route path="/admin/authorize-net-transactions" element={<Protected permission={PERMISSIONS.ADMIN_PANEL}><AuthorizeNetTransactionsPage /></Protected>} />
              <Route path="/billing" element={<Protected permission={PERMISSIONS.BILLING_OWN}><ClientBilling /></Protected>} />
              <Route path="/developer/api-keys" element={<Protected permission={PERMISSIONS.API_KEYS_OWN}><ClientApiKeys /></Protected>} />
            </Route>

            {/* Wide Layout (no max-width cap) */}
            <Route element={<AppLayoutWide />}>
              <Route path="/reports" element={<Protected permission={[PERMISSIONS.REPORTS_ALL, PERMISSIONS.REPORTS_OWN]}><Reports /></Protected>} />
              <Route path="/alerts/ethoca" element={<Protected permission={[PERMISSIONS.ALERTS_ALL, PERMISSIONS.ALERTS_OWN]}><EthocaSwitcher /></Protected>} />
              <Route path="/alerts/cdrn" element={<Protected permission={[PERMISSIONS.ALERTS_ALL, PERMISSIONS.ALERTS_OWN]}><CdrnSwitcher /></Protected>} />
              <Route path="/alerts/rdr-notices" element={<Protected permission={[PERMISSIONS.ALERTS_ALL, PERMISSIONS.ALERTS_OWN]}><RdrSwitcher /></Protected>} />
              <Route path="/alerts/fraud-notices" element={<Protected permission={[PERMISSIONS.ALERTS_ALL, PERMISSIONS.ALERTS_OWN]}><FraudSwitcher /></Protected>} />
              <Route path="/alerts/dispute-notices" element={<Protected permission={[PERMISSIONS.ALERTS_ALL, PERMISSIONS.ALERTS_OWN]}><DisputeSwitcher /></Protected>} />
              <Route path="/alerts/order-insight" element={<Protected permission={[PERMISSIONS.OI_ALL, PERMISSIONS.OI_OWN]}><OrderInsightRouteGuard /></Protected>} />
              <Route path="/alerts/ce-notices" element={<Protected permission={PERMISSIONS.ALERTS_ALL}><CeNotices /></Protected>} />
              <Route path="/alerts/billing-credits" element={<Protected permission={PERMISSIONS.BILLING_ALL}><BillingCredits /></Protected>} />
              <Route path="/alerts/logs" element={<Protected permission={PERMISSIONS.ALERTS_ALL}><AlertLogs /></Protected>} />
              <Route path="/alerts/enrolments" element={<Protected permission={[PERMISSIONS.ENROLMENT_READ_ALL, PERMISSIONS.ENROLMENT_WRITE_ALL, PERMISSIONS.ENROLMENT_OWN]}><EnrolmentSwitcher /></Protected>} />
              <Route path="/evidence-builder" element={<Protected permission={PERMISSIONS.DISPUTE_EVIDENCE}><EvidenceBuilder /></Protected>} />
              <Route path="/evidence-builder/:bankCaseId" element={<Protected permission={PERMISSIONS.DISPUTE_EVIDENCE}><DisputeEvidence /></Protected>} />
              <Route path="/admin/evidence-activity" element={<Protected permission={PERMISSIONS.EVIDENCE_ACTIVITY}><AdminEvidenceActivity /></Protected>} />
              <Route element={<FieldTheme><Outlet /></FieldTheme>}>
                <Route path="/onboarding" element={<Protected permission={PERMISSIONS.COMPANIES_WRITE_ALL}><OnboardingDashboard /></Protected>} />
                <Route path="/onboarding/new" element={<Protected permission={PERMISSIONS.COMPANIES_WRITE_ALL}><OnboardingNew /></Protected>} />
                <Route path="/onboarding/new/:companyId" element={<Protected permission={PERMISSIONS.COMPANIES_WRITE_ALL}><OnboardingNew /></Protected>} />
                <Route path="/onboarding/:onboardingId/edit" element={<Protected permission={PERMISSIONS.COMPANIES_WRITE_ALL}><OnboardingNew /></Protected>} />
                <Route path="/onboarding/:onboardingId" element={<Protected permission={PERMISSIONS.COMPANIES_WRITE_ALL}><OnboardingChecklist /></Protected>} />
              </Route>
              <Route path="/disputes/:disputeId/evidence" element={<Protected permission={PERMISSIONS.DISPUTE_EVIDENCE}><DisputeEvidence /></Protected>} />
              <Route path="/inquiries/:disputeId?" element={<Protected permission={PERMISSIONS.DISPUTE_EVIDENCE_SUBMIT}><PaypalInquiries /></Protected>} />
            </Route>
          </Route>

          {/* Auth Layout */}
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signout-signin" element={<SignOutSignIn />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/shopify-install-error" element={<ShopifyInstallError />} />

          {/* Fallback Route */}
          <Route path="*" element={<NotFound />} />
        </SentryRoutes>
      </Router>
    </>
  );
}
